import { useEffect } from 'react';
import cn from 'clsx';
import { faBook } from '@fortawesome/pro-light-svg-icons';
import { faCartPlus } from '@fortawesome/pro-regular-svg-icons';
import { useCart } from '@v2/shared/lib/cart';
import Alert from '@v2/shared/components/Alert';
import Badge from '@v2/shared/components/Badge';
import Breadcrumbs, { BreadcrumbItem } from '@v2/shared/components/Breadcrumbs';
import Button from '@v2/shared/components/Button';
import Msrp from '@v2/shared/components/Msrp';
import ProductImage from '@v2/shared/components/ProductImage';
import ReviewStars from '@v2/shared/components/ReviewStars';
import SalePrice from '@v2/shared/components/SalePrice';
import Savings from '@v2/shared/components/Savings';
import { Category, ProductType } from '@v2/shared/lib/types';
import useIntersectionObserver from '@v2/shared/lib/useIntersectionObserver';
import { isNamedFormat } from '@v2/shared/lib/utils';
import ActivatingOffer from './ActivatingOffer';
import Authors from './Authors';
import FormatSelector from './FormatSelector';
import Guarantees from './Guarantees';
import LookInside from './LookInside';
import ProductTabs from './ProductTabs';
import WalletButtons from './WalletButtons';
import useProductPage from './useProductPage';
import useStickyOffer from './useStickyOffer';
import { Props } from './types';

/**
 * The book / Misc. Goods product page, previously served by headless_jshop at
 * /products/{slug}.
 *
 * The product arrives once, on the props, and is never fetched again — see
 * ProductPagePropsBuilder for why headless refetched it after mount and why this must
 * not. Everything that is bought or indexed renders from what the server already
 * resolved.
 *
 * Two columns: the cover with its Look Inside pill, and everything that is bought or
 * read. Under them the description and details tabs, and on the phone a sticky bar that
 * appears once the Add to Cart button has scrolled away.
 */
export default function Product({ product, offerCodeSkus, offerCode, selectedSkuCode, payment }: Props) {
  const [addToCartRef, isAddToCartInView] = useIntersectionObserver();
  const { loaded } = useCart();
  const offer = useStickyOffer({ product, offerCode, offerCodeSkus });
  const {
    activeSkus,
    skuProducts,
    selectedSku,
    variantSelectors,
    hasVariants,
    selectVariant,
    addToCart,
    isAdding,
    error,
    dispatchProductViewEvent,
  } = useProductPage({
    product,
    offerCodeSkus: offer.offerCodeSkus,
    offerCode: offer.offerCode,
    selectedSkuCode,
  });

  /**
   * Fire the GA4 view_item once, after the cart context has settled — the same point
   * headless_jshop's ProductView fired it (`!isLoading` there, `loaded` here). Books
   * pushed no ecommerce event at all before this; magazine PDPs already emit the same
   * shape, so downstream matching is fed the book's hex offer_id from one source.
   */
  useEffect(() => {
    if (loaded) {
      dispatchProductViewEvent();
    }
  }, [loaded]);

  /**
   * `selectedSku` is undefined for a product with no sellable sku at all — see
   * useProductPage. Everything that quotes a price is gated on it as well as on
   * `buyable`, so that state renders the "no longer available" alert rather than
   * throwing.
   */
  const canBuy = product.buyable && !!selectedSku;
  const isBook = product.product_type === ProductType.TYPE_BOOK;
  const isPhysicalGood = isBook || product.product_type === ProductType.TYPE_MISCELLANEOUS_GOODS;
  const authors: string[] | undefined = product.genome_attributes?.authors;
  const isbn: string | undefined = product.genome_attributes?.isbn_13;
  const reviews = product.reviews_data;

  // The box, its measure and its spacing are all Layouts.Full's.
  return (
    <>
      <Breadcrumbs items={buildBreadcrumbs(product.category, product.name)} />

      <div className="grid grid-cols-12 gap-0 md:gap-8 lg:gap-12 md:mb-10">
        <div className="mb-4 col-span-8 col-start-3 md:col-start-1 md:col-span-4 lg:col-span-3">
          {/*
            The positioning context for the Look Inside pill, and deliberately wrapping
            the cover alone: give it a margin and "the bottom of the image" stops being
            the bottom of this box.
          */}
          <div className="relative">
            <ProductImage product={product} className="w-full" />
            {isBook && isbn && <LookInside isbn={isbn} />}
          </div>
        </div>

        <div className="relative text-center col-span-12 md:col-span-8 lg:col-span-9">
          {isBook && (
            <Badge padding="snug" pill icon={faBook} className="text-xs mb-2">
              Book
            </Badge>
          )}

          <h1 className="font-bold text-xl md:text-3xl">{product.name}</h1>

          {authors && authors.length > 0 && (
            <div className="text-sm md:text-base inline-block">
              <span className="text-slate-600 font-medium">by</span>{' '}
              <span className="font-semibold text-slate-800">
                <Authors authors={authors} />
              </span>
            </div>
          )}

          <div className="mt-3 mb-5 md:mb-8 text-sm md:text-lg">
            {!!reviews && reviews.count > 0 && (
              <ReviewStars
                rating={reviews.rating}
                reviewsCount={reviews.count}
                showReviewsLabel
                onClick={() => document.getElementById('reviews')?.scrollIntoView({ block: 'start' })}
              />
            )}

            {canBuy && (
              <div className="mt-6 md:mt-8 mb-6 md:mb-8 text-center">
                {!hasVariants && isNamedFormat(selectedSku.text) && (
                  <div className="font-semibold leading-tight text-lg">{selectedSku.text}</div>
                )}
                <div className="flex flex-col">
                  <div>
                    <SalePrice sku={selectedSku} className="text-3xl" />
                  </div>
                  {/* Above the price when a chooser sits below it, under it otherwise —
                      so the list price is never the last thing before the buttons. */}
                  <Msrp
                    sku={selectedSku}
                    productType={product.product_type}
                    showLabel
                    className={cn('block', hasVariants ? 'order-first' : 'order-last')}
                  />
                </div>
                <Savings sku={selectedSku} className="block" showAmount />
              </div>
            )}
          </div>

          {!canBuy && (
            <Alert color="danger" className="text-center mb-8 md:mb-3">
              {product.digital
                ? 'Unfortunately this title is temporarily unavailable. Please check again at a later date.'
                : 'Unfortunately this title is no longer available'}
            </Alert>
          )}

          {canBuy && (
            <>
              {hasVariants && (
                // The server's code, never a sticky one: that lives in the session, and
                // the page ignores it in a url — see ProductPagePropsBuilder.
                <FormatSelector
                  selectors={variantSelectors}
                  activeSkus={activeSkus}
                  selectedSku={selectedSku}
                  skuProducts={skuProducts}
                  offerCode={offerCode}
                  selectVariant={selectVariant}
                />
              )}

              {isPhysicalGood && <div className="text-sm mb-3">Usually Ships in 1-2 Business Days</div>}

              <div className="max-w-lg mx-auto mb-5">
                <div ref={addToCartRef}>
                  <Button
                    fullWidth
                    className="mb-3"
                    icon={faCartPlus}
                    onClick={() => addToCart()}
                    isLoading={isAdding}
                    loadingText="Adding..."
                  >
                    Add to Cart
                  </Button>
                </div>
                <div className="grid lg:grid-flow-col auto-cols-fr gap-3 mb-3">
                  <WalletButtons
                    payment={payment}
                    product={product}
                    sku={selectedSku}
                    submitPayload={addToCart}
                  />
                </div>
                <Guarantees />
              </div>

              {error && (
                <Alert color="danger" className="mb-8">
                  {addToCartMessage(error)}
                </Alert>
              )}
            </>
          )}

          {canBuy && offer.isActivating && <ActivatingOffer />}
        </div>
      </div>

      <ProductTabs product={product} />

      {/* Slid out of view rather than unmounted once it exists: the wallet buttons
          inside it take a moment to initialize, and remounting them on every scroll
          would restart that. A page with nothing to sell has no bar at all, and it stays
          down while an offer activates, which ActivatingOffer cannot cover. */}
      {canBuy && (
        <div
          className={cn(
            'fixed bottom-0 inset-x-0 bg-white px-5 pt-3 pb-4 border-t-[1.5px] border-slate-200',
            'transition duration-300 md:hidden z-20',
            isAddToCartInView || offer.isActivating ? 'translate-y-[138px]' : 'translate-y-0'
          )}
        >
          <div className="text-center flex flex-col items-center justify-center mb-3">
            {isNamedFormat(selectedSku.text) && <div className="font-semibold leading-tight">{selectedSku.text}</div>}
            <div className="flex items-center w-full justify-center">
              <SalePrice sku={selectedSku} className="text-xl" />
              <Msrp sku={selectedSku} productType={product.product_type} className="mr-2" />
              <Savings sku={selectedSku} className="text-sm" />
            </div>
          </div>

          {/* Only the first two children are shown — see .product-view-floating-cta in
              v2.css. Add to Cart is always one of them; the other is Apple Pay where the
              device offers it and Google Pay otherwise, which is the order WalletButtons
              renders them in. */}
          <div className="grid grid-flow-col gap-2 product-view-floating-cta">
            <Button
              fullWidth
              icon={faCartPlus}
              onClick={() => addToCart()}
              isLoading={isAdding}
              loadingText="Adding..."
            >
              Add to Cart
            </Button>
            <WalletButtons
              payment={payment}
              product={product}
              sku={selectedSku}
              submitPayload={addToCart}
              compact
            />
          </div>
        </div>
      )}
    </>
  );
}

Product.layoutName = 'Layouts.Full';

/**
 * Home, then the category trail, then the product — the trail walked from the leaf
 * category up through its parents, which is the only direction the payload links in.
 *
 * The last crumb carries no href, which is what Breadcrumbs wants: Google falls back to
 * the page's own url for it.
 */
function buildBreadcrumbs(category: Category | undefined, name: string): BreadcrumbItem[] {
  const trail: BreadcrumbItem[] = [];

  for (let current = category; current; current = current.parent) {
    trail.unshift({ label: current.name, href: current.href });
  }

  return [{ label: 'Home', href: '/' }, ...trail, { label: name }];
}

/** The two failures the cart names, and a catch-all for everything else. */
function addToCartMessage(error: string): string {
  if (error.includes('LIMITEDSTOCK')) {
    return 'Due to limited stock availability, this product could not be added to your cart.';
  }

  if (error.includes('OUTOFSTOCK')) {
    return 'This product is no longer available and could not be added to your cart.';
  }

  return 'Oops! Could not add product to cart. Please try again.';
}
